import { useState, useEffect, useCallback } from "react";
import { getSOPMedia, SOPMedia } from "../api/sopMedia";
import { filterSOPMedia, FilteredMedia } from "../utils/mediaFiltering";

// Simple module-level cache so multiple components using useSopMedia for the same
// sopId share a single backend fetch (e.g. SOPEditor + DocumentPreviewAside).
const mediaCache = new Map<string, FilteredMedia | null>();
const loadingSops = new Set<string>();

interface UseSopMediaOptions {
  enabled?: boolean;
  includeExtracted?: boolean;
}

const normalizeMediaForContext = (
  media: SOPMedia[],
  includeExtracted: boolean,
): SOPMedia[] => {
  if (includeExtracted) return media;
  // Reference panel does not need extracted frames/renders.
  return media.map((item) => ({ ...item, extracted: undefined }));
};

export const useSopMedia = (
  sopId: string | null,
  options?: UseSopMediaOptions,
) => {
  const enabled = options?.enabled ?? true;
  const includeExtracted = options?.includeExtracted ?? true;
  const cacheKey = `${sopId ?? ""}::${includeExtracted ? "full" : "lite"}`;
  const [media, setMedia] = useState<FilteredMedia | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const doFetch = useCallback(
    async (force: boolean) => {
      if (!sopId) return;
      if (!enabled && !force) return;

      if (!force) {
        // If we already have cached media for this SOP, use it and skip fetching.
        if (mediaCache.has(cacheKey)) {
          setMedia(mediaCache.get(cacheKey) ?? null);
          setError(null);
          setLoading(false);
          return;
        }

        // Avoid duplicate in-flight requests for the same sopId.
        if (loadingSops.has(sopId)) {
          return;
        }
      }

      try {
        loadingSops.add(sopId);
        setLoading(true);
        setError(null);
        const response = await getSOPMedia(sopId);
        const normalized = normalizeMediaForContext(
          response.original_media,
          includeExtracted,
        );
        const filtered = filterSOPMedia(normalized);
        mediaCache.set(cacheKey, filtered);
        setMedia(filtered);
      } catch (err) {
        mediaCache.delete(cacheKey);
        setError(err instanceof Error ? err.message : "Failed to load media");
      } finally {
        loadingSops.delete(sopId);
        setLoading(false);
      }
    },
    [sopId, enabled, includeExtracted, cacheKey],
  );

  const loadMedia = useCallback(() => {
    if (!enabled) return;
    void doFetch(false);
  }, [doFetch, enabled]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  return {
    media,
    loading,
    error,
    // Refetch should always perform a real backend call (tests rely on this),
    // while initial/auto loads share a cached response when available.
    refetch: () => doFetch(true),
  };
};